import {ChangeDetectionStrategy, Component, computed, input, output} from '@angular/core';
import {formatDate} from '@angular/common';
import {saveAs} from 'file-saver';
import {
    buildEventsCsv,
    eventKey,
    formatTime,
    groupByDay,
    groupBySave,
    HistoryEvent,
    kindBadgeClass,
    kindCalloutClass,
    tableDisplay
} from '@app/_components/history-drawer/history-drawer.helpers';
import {formatDateTime} from '@app/processing/request-history/request-history.helpers';

@Component({
    selector: 'app-history-drawer',
    templateUrl: './history-drawer.component.html',
    styleUrl: './history-drawer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HistoryDrawerComponent {
    readonly title = input.required<string>();
    /** :param exportName: the CSV file name before its timestamp, e.g. 'feature-flag-history'. */
    readonly exportName = input.required<string>();
    readonly events = input.required<HistoryEvent[]>();
    /** :param layout: 'timeline' shows one callout per event; 'saves' shows one callout per save (correlationId). */
    readonly layout = input<'timeline' | 'saves'>('timeline');
    readonly loading = input(false);
    readonly loadFailed = input(false);
    readonly hasMore = input(false);
    /** :param showEmptyMessage: false while the page shows its own error, so "No logged changes." is not claimed. */
    readonly showEmptyMessage = input(true);

    readonly loadMore = output<void>();
    readonly retry = output<void>();
    readonly closed = output<void>();

    protected readonly days = computed(() => groupByDay(this.events()));
    protected readonly saveDays = computed(() => groupBySave(this.events()));

    protected readonly eventKey = eventKey;
    protected readonly tableDisplay = tableDisplay;
    protected readonly kindBadgeClass = kindBadgeClass;
    protected readonly kindCalloutClass = kindCalloutClass;
    protected readonly formatTime = formatTime;
    protected readonly formatDateTime = formatDateTime;

    exportCsv() {
        const timestamp = formatDate(new Date(), 'yyyyMMdd-HHmm', 'en-US');
        // Leading BOM so Excel on Windows opens the file as UTF-8 instead of the system codepage.
        this.saveFile(new Blob(['﻿' + buildEventsCsv(this.events())], {type: 'text/csv;charset=utf-8;'}),
            `${this.exportName()}-${timestamp}.csv`);
    }

    // Spec files share one module cache, so a spec cannot reliably vi.mock file-saver; it spies on this instead.
    protected saveFile(blob: Blob, fileName: string) {
        saveAs(blob, fileName);
    }
}
